import React, { useMemo } from 'react';

import { Link } from 'react-router-dom';

import { css } from '@emotion/css';
import { TransferThresholdSettings } from '@recurrency/core-api-schema/dist/common/enums';
import { TransferTargetLineDTO } from '@recurrency/core-api-schema/dist/transferOrders/common';

import { convertTransferQtyToUom, getSelectedUomAndUnitSize } from 'pages/purchasing/TransferReplenishmentPage/utils';

import { calcLine, calcOp, calcProp, Equation } from 'components/recipes/equation/Equation';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { truthy } from 'utils/boolean';
import { formatNumber } from 'utils/formatting';
import { isAdmin } from 'utils/roleAndTenant';
import { routes } from 'utils/routes';

import { EquationPopover } from './EquationPopover';

export const QtyTransferablePopover = ({ record }: { record: TransferTargetLineDTO }) => {
  const { activeErpRole } = useGlobalApp();
  const isUserAdminRole = isAdmin(activeErpRole.foreignId, activeErpRole.name);

  const { qtyTransferable, sourceTransferCriteria, sourceMin, sourceMax, sourceNetStock } = record;
  const { selectedUnitSize, selectedUom } = getSelectedUomAndUnitSize(record);

  const thresholdCriteriaNumber =
    sourceTransferCriteria === TransferThresholdSettings.AboveMin
      ? sourceMin
      : TransferThresholdSettings.AboveMax
      ? sourceMax
      : undefined;

  const qtyTransferableValue = useMemo(
    () => Math.floor(qtyTransferable / selectedUnitSize),
    [qtyTransferable, selectedUnitSize],
  );
  const showThresholdCriteria =
    !!sourceTransferCriteria &&
    sourceTransferCriteria !== TransferThresholdSettings.NetStock &&
    thresholdCriteriaNumber !== undefined;

  const calcOperation = useMemo(() => {
    const thresholdCriteriaLabels = {
      [TransferThresholdSettings.AboveMin]: 'Source Min',
      [TransferThresholdSettings.AboveMax]: 'Source Max',
    };

    const baseCalculationOperations = [
      calcProp(`Transferable`, `${formatNumber(qtyTransferableValue)} ${selectedUom}`),
      calcOp('='),
      calcOp('Max ('),
      calcProp(
        `Net Stock`,
        `${formatNumber(convertTransferQtyToUom(sourceNetStock, selectedUnitSize))} ${selectedUom}`,
      ),
      showThresholdCriteria ? calcOp('-') : null,
      showThresholdCriteria
        ? calcProp(
            thresholdCriteriaLabels[sourceTransferCriteria],
            `${formatNumber(convertTransferQtyToUom(thresholdCriteriaNumber, selectedUnitSize))} ${selectedUom}`,
          )
        : null,
      calcOp(', 0)'),
    ].filter(truthy);

    return baseCalculationOperations;
  }, [
    qtyTransferableValue,
    selectedUnitSize,
    selectedUom,
    showThresholdCriteria,
    sourceNetStock,
    sourceTransferCriteria,
    thresholdCriteriaNumber,
  ]);

  return (
    <EquationPopover title="Quantity Transferable Breakdown">
      <Equation lines={[calcLine(...calcOperation)]} />
      <div
        className={css`
          margin-top: 8px;
        `}
      >
        The amount currently transferrable from the source location using the transfer threshold defined in Location
        Settings.
      </div>
      {isUserAdminRole && <Link to={routes.settings.locationSettings}>Go to Location Settings</Link>}
    </EquationPopover>
  );
};
